"use client";

import React, { ReactNode, useEffect, useState } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { Copilot } from "@acko-swat-sdk/ui";
import "@acko-swat-sdk/ui/dist/index.css";
import styles from "./CopilotBot.module.scss";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getLoginUrl } from "@utils/getLoginUrl";
import { postLead } from "@services/leadCaptureService";
import { getCopilotUrl } from "@helpers/copilotHelper";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { getPlatform } from "@utils/platformDetection.client";
import { getPageNameFromUrl } from "@utils/pageNameUtils";
import { useAppSelector } from "@lib/redux/hooks";
import { getCityFromIp } from "@utils/cityDetection";

declare global {
  interface Window {
    copilot: {
      open: () => void;
      close: () => void;
      setContext: (context: unknown) => void;
      sendMessage: (message: string) => void;
      getSummary: () => string;
    };
  }
}

interface CopilotLeadData {
  summary?: any;
  phone_number?: any;
  make?: string;
  model?: string;
  variant?: string;
  colour?: string;
  city?: string;
  ip_city?: any;
  action_name?: string;
  [key: string]: any;
}

interface CallLSQOptions {
  leadData: Partial<CopilotLeadData>;
  actionName?: string;
  customTrackingProps?: Record<string, any>;
  pageType?: string;
  loginType?: string;
  intent?: string;
  carData?: {
    make?: string;
    model?: string;
    variant?: string;
    color?: string;
  };
}

const CopilotBot = ({
  children,
  isLoggedIn,
  initialProfile,
  cityName,
}: {
  children: ReactNode;
  isLoggedIn: any;
  initialProfile: any;
  cityName: any;
}) => {

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const source = searchParams.get("source");
  const copilotCarData = useAppSelector(state => state.carData.copilotCarData);

  const [copilotIsInitialised, setCopilotIsInitialised] = useState(false);
  const [showCopilot, setShowCopilot] = useState<boolean>(false);
  const [copilotUrl, setCopilotUrl] = useState<any>();

  const callLSQ = async ({ 
    leadData, 
    customTrackingProps = {},
    loginType = "Request callback",
    intent = "",
    carData
  }: CallLSQOptions): Promise<void> => {
    
    const baseLeadData = {
      url: window.location.href,
      platform: getPlatform(),
      page_type: getPageNameFromUrl(pathname),
      login_type: loginType,
      intent,
      ...(carData?.make && { make: carData.make }),
      ...(carData?.model && { model: carData.model }),
      ...(carData?.variant && { variant: carData.variant }),
      ...(carData?.color && { colour: carData.color }),
      ...leadData 
    };

    const leadResponse = await postLead(addUTMParamsToLeadData(baseLeadData));

    const trackingData = {
      action_type: leadResponse?.data?.is_created ? "Lead Created" : "Lead Updated",
      action_name: "chatbot",
      section: "chatbot",
      url: window.location.href,
      platform: getPlatform(),
      page_type: getPageNameFromUrl(pathname),
      login_type: loginType,
      intent,
      ...(carData?.make && { make: carData.make }),
      ...(carData?.model && { model: carData.model }),
      ...(carData?.variant && { variant: carData.variant }),
      ...(carData?.color && { colour: carData.color }),
      ...leadData,
      ...customTrackingProps 
    };

    trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: getPageNameFromUrl(pathname),
      additionalProps: trackingData,
    }); 
  };
  
  useEffect(() => {
    const fetchCopilotUrl = async () => {
      try {
        const fetchedCopilotUrl = await getCopilotUrl();
        setCopilotUrl(fetchedCopilotUrl);
      } catch (error) {
        console.error('Error fetching copilot URL:', error);
      }
    };

    fetchCopilotUrl();
  }, []);

  useEffect(() => {
    const handleEffect = async () => {
      if (source == "copilot" && copilotIsInitialised) {
        (window as any).copilot?.open();
        const summary = window.copilot.getSummary();
        await callLSQ({ 
          leadData: { 
            summary,
            phone: initialProfile.phone || initialProfile.contact_no,
            city: cityName,
            ip_city: await getCityFromIp()
          },
          carData: copilotCarData
        });
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("source");
        router.replace(`?${newParams.toString()}`, { scroll: false });
      }
    };
    handleEffect();
  }, [source, copilotIsInitialised]);

  const handleAgentEvent = async() => {
    if (!isLoggedIn) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("source", "copilot");
      window.location.href = getLoginUrl(currentUrl.toString());
    } else {
      const summary = window.copilot.getSummary();
      callLSQ({ 
        leadData: { 
          summary,
          phone: initialProfile.phone || initialProfile.contact_no,
          city: cityName,
          ip_city: await getCityFromIp()
        },
        carData: copilotCarData
      });
    }
  };

  const handleCopilotEvent = async (type: any, payload: unknown) => {
    switch (type) {
      case "feedbackCopilot":
        await trackEventWithCommonProps("feedbackCopilot", EventType.TRACK_EVENT, {
          additionalProps: {
            payload,
          },
        });
        break;
      case "connection-initialised-completely":
      case "connection-established-successfully":
        setCopilotIsInitialised(true);
        break;
      case "join-conversation":
        break;
      case "closeCopilot":
        setShowCopilot(false);
        await trackEventWithCommonProps("closeCopilot", EventType.TRACK_EVENT, {
          additionalProps: {
            payload,
          },
        });
        break;
      case "openCopilot":
        setShowCopilot(true);
        await trackEventWithCommonProps("openCopilot", EventType.TRACK_EVENT, {
          additionalProps: {
            payload,
          },
        });
        break;
      case "summary":
        break;
      case "login":
        break;
      case "agent":
        handleAgentEvent();
        break;
      case "message":
        break;
    }
  };

  return (
    <>
      {children}
      <div className={showCopilot ? styles.copilotMainContainer : styles.copilotHiddenContainer}>
        <Copilot url={copilotUrl} fabHide={true} onEvent={handleCopilotEvent} />
      </div>
    </>
  );
};

export default CopilotBot;
